import {isFeatureEnabled} from '@github-ui/feature-flags'
import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {sanitize} from '@github-ui/sanitize'
import type {UpdateLabelMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelMutation'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {
  buildConfirmedEditLabelOperation,
  buildEditLabelOperation,
  type LabelInput,
  labelInputFromOperation,
  LABELS_CONNECTION_PATH,
  LABELS_OPTIMISTIC_CONNECTION_FLAG,
} from './utils/label-connection-operations'
import {updateLabelRequest} from './utils/label-requests'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseEditLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setEditSubmissionErrors: (v: string) => void
  setIsEditDialogOpen: (v: boolean) => void
}

type EditLabelHandler = (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => void

type EditLabelContext = {
  previousData: LabelsQueryData | undefined
}

/** Selects the edit strategy by feature flag; see `useCreateLabel` for the both-mounted pattern. */
export function useEditLabel(options: UseEditLabelOptions): EditLabelHandler {
  const legacy = useEditLabelLegacy(options)
  const optimistic = useEditLabelOptimistic(options)
  return isFeatureEnabled(LABELS_OPTIMISTIC_CONNECTION_FLAG) ? optimistic : legacy
}

function useEditLabelOptimistic({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setEditSubmissionErrors,
  setIsEditDialogOpen,
}: UseEditLabelOptions): EditLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useConnectionMutation<UpdateLabelMutationMutation, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    // The operation's fields carry the raw input the write needs.
    mutationFn: async operation => {
      const input = labelInputFromOperation(operation)
      if (!input) throw new Error(LABELS.editLabelError)
      return updateLabelRequest(repositoryNameWithOwner, labelId, input)
    },
    onError: error => {
      setEditSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setIsEditDialogOpen(true)
    },
    onSuccess: data => {
      const updatedLabel = data.updateLabel?.label
      if (queryKey && updatedLabel) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildConfirmedEditLabelOperation(labelId, updatedLabel)) : base,
        )
      }
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      setEditSubmissionErrors('')
      setIsEditDialogOpen(false)
      mutate({operation: buildEditLabelOperation(labelId, input)})
    },
    [mutate, labelId, setEditSubmissionErrors, setIsEditDialogOpen],
  )
}

/** The flag-off fallback: hand-rolled cache surgery with snapshot rollback. */
function useEditLabelLegacy({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setEditSubmissionErrors,
  setIsEditDialogOpen,
}: UseEditLabelOptions): EditLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<UpdateLabelMutationMutation, Error, LabelInput, EditLabelContext>({
    mutationFn: (input: LabelInput) => updateLabelRequest(repositoryNameWithOwner, labelId, input),
    onMutate: async (input): Promise<EditLabelContext> => {
      setEditSubmissionErrors('')

      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically update the label in the TanStack cache
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === labelId)
            if (edge?.node) {
              edge.node.name = input.name
              edge.node.nameHTML = sanitize(input.name, {
                ALLOWED_TAGS: [],
                ALLOWED_ATTR: [],
                ALLOW_ARIA_ATTR: false,
                RETURN_TRUSTED_TYPE: false,
              })
              edge.node.description = input.description
              edge.node.color = input.color
            }
          }),
        )
      }

      // Optimistically close the dialog
      setIsEditDialogOpen(false)

      return {previousData}
    },
    onError: (error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setEditSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setIsEditDialogOpen(true)
    },
    onSuccess: data => {
      const updatedLabel = data.updateLabel?.label
      if (queryKey && updatedLabel) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === labelId)
            if (edge?.node) {
              edge.node.name = updatedLabel.name
              edge.node.nameHTML = updatedLabel.nameHTML
              edge.node.description = updatedLabel.description
              edge.node.color = updatedLabel.color
            }
          }),
        )
      }
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
